import { LanguageKeys } from 'src/common/constants/languages';
import {
  IPageBlock,
  ITextBlock,
  IVisibilityFilter,
} from 'src/interfaces/PageBlock';
import {
  getBodyBlocks,
  IPageDocument,
  normalizePageDocument,
  pageContentFromDocument,
  updatePageBlock,
} from 'src/helpers/pageDocumentUtils';
import {
  IBasePageContent,
  ILegacyPageContentSection,
  IPageContent,
} from 'src/interfaces/PageContent';

import { VISIBILITY_PRESETS, VisibilityPresetId } from './pageContentBlockPresets';

import { PublicPageShell } from 'src/interfaces/PublicPage';

export function shouldApplyBlockVisibilityRules(
  _contentKeyOrShell?: string,
  _layoutShell?: PublicPageShell | null,
): boolean {
  return true;
}

const COMPETITIONS_LIST_TOKEN = /\{\{\s*CompetitionsList\s*\}\}/gi;

const emptyI18n = (): Record<LanguageKeys, string> => ({ en: '', hu: '', ro: '' });

function stripCompetitionsListToken(html: string): string {
  return html.replace(COMPETITIONS_LIST_TOKEN, '').trim();
}

export function sortPageBlocks(blocks: IPageBlock[]): IPageBlock[] {
  return [...blocks].sort((a, b) => a.sortOrder - b.sortOrder);
}

function legacySectionsToBlocks(sections: ILegacyPageContentSection[]): IPageBlock[] {
  return sections.map((section, index) => ({
    id: `legacy-${section.name}`,
    type: 'text' as const,
    sortOrder: index,
    i18n: { ...emptyI18n(), ...section.i18n },
    legacyKey: section.name,
  }));
}

function stripCompetitionsListFromTextBlock(block: ITextBlock): ITextBlock {
  const i18n = { ...block.i18n };
  (['en', 'hu', 'ro'] as LanguageKeys[]).forEach(lang => {
    i18n[lang] = stripCompetitionsListToken(i18n[lang] ?? '');
  });
  return { ...block, i18n };
}

/**
 * When blocks[] exists but has no text (e.g. after a bad migration split), recover
 * copy from legacy content[] so the dashboard and public site stay in sync.
 */
function mergeLegacyContentIntoBlocks(
  blocks: IPageBlock[],
  sections: ILegacyPageContentSection[],
): IPageBlock[] {
  const hasTextBlock = blocks.some(b => b.type === 'text');
  if (hasTextBlock || !sections.length) {
    return blocks;
  }

  const hasCompetitionsListBlock = blocks.some(b => b.type === 'competitionsList');
  const legacyText = legacySectionsToBlocks(sections)
    .filter((b): b is ITextBlock => b.type === 'text')
    .map(block =>
      hasCompetitionsListBlock ? stripCompetitionsListFromTextBlock(block) : block,
    )
    .filter(block =>
      (['en', 'hu', 'ro'] as LanguageKeys[]).some(lang => block.i18n[lang]?.trim()),
    );

  if (!legacyText.length) {
    return blocks;
  }

  const offset = legacyText.length;
  const shiftedBlocks = blocks.map(b => ({ ...b, sortOrder: b.sortOrder + offset }));
  return sortPageBlocks([...legacyText, ...shiftedBlocks]);
}

export function getBlocksFromPageContent(
  pageContent: IBasePageContent | undefined,
): IPageBlock[] {
  if (!pageContent) return [];

  const doc = normalizePageDocument(pageContent);
  const fromBlocks = getBodyBlocks(doc).length
    ? getBodyBlocks(doc)
    : pageContent.blocks?.length
      ? sortPageBlocks(pageContent.blocks.filter(b => b.zone !== 'hero'))
      : [];
  const fromLegacy = pageContent.content?.length
    ? legacySectionsToBlocks(pageContent.content)
    : [];

  if (!fromBlocks.length) {
    return fromLegacy;
  }

  if (pageContent.content?.length) {
    return mergeLegacyContentIntoBlocks(fromBlocks, pageContent.content);
  }

  return fromBlocks;
}

export function applyVisibilityPreset(presetId: VisibilityPresetId): IVisibilityFilter {
  return JSON.parse(JSON.stringify(VISIBILITY_PRESETS[presetId]));
}

export function hasActiveVisibilityRules(filter: IVisibilityFilter | undefined): boolean {
  return Boolean(filter?.rules?.length);
}

/** Applies a block patch and removes `visibility` when it is cleared in the editor. */
export function applyPageBlockPatch(block: IPageBlock, patch: Partial<IPageBlock>): IPageBlock {
  const next = { ...block, ...patch } as IPageBlock;
  if ('visibility' in patch && patch.visibility === undefined) {
    delete next.visibility;
  }
  // Columns layout editor patches layout fields only; keep block identity.
  next.id = block.id;
  next.type = block.type;
  next.sortOrder = block.sortOrder;
  return next;
}

/** Keeps array order; only updates sortOrder fields (e.g. after drag reorder). */
export function assignBlockSortOrders(blocks: IPageBlock[]): IPageBlock[] {
  return blocks.map((block, index) => ({
    ...block,
    sortOrder: index,
  }));
}

export function reindexBlocks(blocks: IPageBlock[]): IPageBlock[] {
  return sortPageBlocks(blocks).map((block, index) => ({
    ...block,
    sortOrder: index,
  }));
}

export function updateTextBlockI18n(
  doc: IPageDocument,
  blockId: string,
  lang: LanguageKeys,
  html: string,
): IPageDocument {
  let found: ITextBlock | undefined;
  for (const top of doc.blocks) {
    if (top.id === blockId && top.type === 'text') {
      found = top as ITextBlock;
      break;
    }
    if (top.type === 'columns') {
      for (const col of top.columns) {
        const nested = col.blocks.find(b => b.id === blockId && b.type === 'text');
        if (nested) {
          found = nested as ITextBlock;
          break;
        }
      }
    }
    if (found) break;
  }
  if (!found) return doc;
  return updatePageBlock(doc, blockId, {
    i18n: { ...found.i18n, [lang]: html },
  });
}

export function findPageBlockById(
  doc: IPageDocument,
  blockId: string,
): IPageBlock | undefined {
  for (const top of doc.blocks) {
    if (top.id === blockId) {
      return top;
    }
    if (top.type === 'columns') {
      for (const col of top.columns) {
        const nested = col.blocks.find(b => b.id === blockId);
        if (nested) return nested;
      }
    }
  }
  return undefined;
}

export function pageContentWithBlocks(
  pageContent: IPageContent | undefined,
  name: string,
  blocks: IPageBlock[],
  heroSettings?: import('src/interfaces/HeroSettings').IHeroSettings,
): IBasePageContent {
  const doc = {
    blocks: reindexBlocks(blocks),
    heroSettings,
  };
  return pageContentFromDocument(doc, pageContent, name);
}
